<script setup>
import { __ } from 'JIX/gettext.js';
import { ref, computed, nextTick, watch } from 'vue';
import FullscreenOverlay from 'JIX/components/FullscreenOverlay.vue';
import DsIconButton from '../ds/DsIconButton.vue';
import { useStore } from 'vuex';
import { useSearchButtonText } from 'JIX/jobsearch/utils.js';
import JobsearchInputField from './JobsearchInputField.vue';
import JobsearchSearchBarGeneral from './JobsearchSearchBarGeneral.vue';
import JobsearchSearchBarGeo from './JobsearchSearchBarGeo.vue';
import JobsearchRenderQuery from './JobsearchRenderQuery.vue';

const store = useStore();

defineProps({
    inputClasses: {
        type: String,
        default: "form-control form-control-lg"
    },
});

const emit = defineEmits(['search', 'resetSearch']);

const showOverlay = ref(store.state.showDropdown || store.state.showDropdownGeo);

const searchbargeneral = ref();
const searchbargeo = ref();
const renderQueryRef = ref();
const bodyContainer = ref();

function firstQueryParamSelected() {
    searchbargeneral.value.showDropdown = false;
    if (!store.getters.selectedValues().geoareaid) {
        searchbargeo.value.searchbar.focusInput();
    }
}

function handleCancelSearch() {
    store.commit('clearSearchQueryWorking');
    showOverlay.value = false;
}

function handleSearchBarGeoFocusIn() {
    searchbargeneral.value?.handleUnhandledInput({ origin: 'searchbargeofocus' });
}

const workingQueryHitcount = computed(() => store.state.workingQueryHitcount);
const jobcountText = useSearchButtonText({ hitcount: workingQueryHitcount });

const inputIcon = {
    size: 'sm',
    name: 'service-jobsearch',
};

watch(
    () => searchbargeo.value?.showDropdown ?? false,
    async (open) => {
        if (open) {
            await nextTick();
            searchbargeo.value.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    },
);

watch (
    () => showOverlay.value,
    async (open) => {
        if (open) {
            await nextTick();
            searchbargeneral.value.focusInput();
            setTimeout(() => { // FIXME: clickOutside is triggered JobsearchDropdown, which sets this to false. If that is fixed we should be able to avoid this timeout.
                searchbargeneral.value.showDropdown = true;
            }, 10);
        }
    },
);

function clickOutsideHeader(){
    renderQueryRef.value.collapseBadges();
}

defineExpose({
    close: () => showOverlay.value = false,
});

function resetAndSetFocus(){
    searchbargeneral.value.focusInput();
    emit('resetSearch');
}
</script>

<template>
    <div class="jobsearch-mobile__input-container">
        <JobsearchInputField
            ref="inputField"
            :aria-label="__('Hvilket job leder du efter?')"
            :placeholder="__('Hvilket job leder du efter?')"
            @focus="showOverlay = true"
            @click="showOverlay = true"
            :input-classes="inputClasses"
            :icon="inputIcon"
        />
    </div>
    <FullscreenOverlay v-model:open="showOverlay">
        <template #header>
            <DsIconButton
                icon-name="button-close"
                type="secondary"
                class="flex-shrink-0"
                size="sm"
                :label="__('Luk')"
                @click.prevent="handleCancelSearch"
                button-type="button"
            />
            <JobsearchRenderQuery
                ref="renderQueryRef"
                show-reset-button-on-expand
                @reset-search="resetAndSetFocus"
            />
        </template>
        <template #body>
            <div class="p-3 d-flex flex-column gap-2 h-100" @click="clickOutsideHeader" ref="bodyContainer">
                <JobsearchSearchBarGeneral
                    class="jobsearch-mobile__search-field-container header-scroll-margin"
                    ref="searchbargeneral"
                    @suggestion-selected.once="firstQueryParamSelected"
                    @suggestion-selected="searchbargeneral.showDropdown = false"
                    :input-classes="'form-control form-control-lg jobsearch-mobile__search-field'"
                    @empty-enter="$emit('search')"
                />
                <JobsearchSearchBarGeo
                    class="jobsearch-mobile__search-field-container header-scroll-margin"
                    ref="searchbargeo"
                    :input-classes="'form-control form-control-lg jobsearch-mobile__search-field'"
                    @suggestion-selected="searchbargeo.showDropdown = false"
                    @empty-enter="$emit('search')"
                    @focusin="handleSearchBarGeoFocusIn"
                />
            </div>
        </template>
        <template #footer>
            <button type="button" class="btn btn-tertiary btn-sm" @click="resetAndSetFocus">
                {{ __('Nulstil') }}
            </button>
            <button type="submit" @click.prevent="$emit('search')" class="btn btn-primary btn-sm">
                {{ jobcountText }}
            </button>
        </template>
    </FullscreenOverlay>
</template>
